import type { CampaignImageContentType } from "@/marketing/models/influencerCampaign";

/** Pixel crop area as produced by react-easy-crop's `croppedAreaPixels`. */
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Thrown when a cropped image cannot be encoded under the byte limit. */
export class ImageTooLargeError extends Error {
  constructor(message = "Cropped image is too large to upload") {
    super(message);
    this.name = "ImageTooLargeError";
  }
}

/** Descending quality steps tried for lossy formats (jpeg/webp). */
export const DEFAULT_QUALITY_STEPS = [0.92, 0.8, 0.7, 0.6, 0.5];

/** Encodes the cropped canvas at a given mime/quality into a Blob. */
export type EncodeFn = (
  mime: CampaignImageContentType,
  quality: number | undefined,
) => Promise<Blob>;

export function validateCropArea(area: Area): void {
  // Require at least 1px: sub-pixel sizes (e.g. 0.4) round to a 0-dimension canvas.
  if (area.width < 1 || area.height < 1) {
    throw new Error(
      `Invalid crop area: width and height must be at least 1 pixel (got ${area.width}x${area.height})`,
    );
  }
}

/**
 * Encodes within `maxBytes`. PNG is lossless so it is encoded once and rejected
 * if too large; lossy formats step the quality down until they fit.
 */
export async function encodeWithSizeLimit(
  mime: CampaignImageContentType,
  maxBytes: number,
  encode: EncodeFn,
  qualitySteps: number[] = DEFAULT_QUALITY_STEPS,
): Promise<Blob> {
  if (mime === "image/png") {
    const blob = await encode(mime, undefined);
    if (blob.size > maxBytes) throw new ImageTooLargeError();
    return blob;
  }

  for (const quality of qualitySteps) {
    const blob = await encode(mime, quality);
    if (blob.size <= maxBytes) return blob;
  }
  throw new ImageTooLargeError();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image for cropping"));
    image.src = src;
  });
}

/**
 * Draws `cropPixels` of `imageSrc` onto a canvas and returns the cropped Blob,
 * re-encoding down to `maxBytes` for lossy formats. The canvas raster work is
 * the imperative shell; the size/format logic lives in `encodeWithSizeLimit`.
 */
export async function getCroppedImageBlob(
  imageSrc: string,
  cropPixels: Area,
  mime: CampaignImageContentType,
  maxBytes: number,
): Promise<Blob> {
  validateCropArea(cropPixels);

  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cropPixels.width);
  canvas.height = Math.round(cropPixels.height);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2D canvas context");

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  const encode: EncodeFn = (encodeMime, quality) =>
    new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error("Canvas toBlob returned null")),
        encodeMime,
        quality,
      );
    });

  return encodeWithSizeLimit(mime, maxBytes, encode);
}
